<template>
  <div class="space-y-6">
    <!-- Page header -->
    <div>
      <h1 class="text-3xl font-bold">Charts</h1>
      <p class="text-muted-foreground">
        Interactive candlestick charts with real-time data
      </p>
    </div>

    <!-- Charts container -->
    <div class="space-y-6">
      <div v-for="chart in charts" :key="chart.id" class="rounded-md border p-4">
        <CandlestickChart :symbol="chart.symbol" @remove="removeChart(chart.id)" />
      </div>

      <!-- Add chart button -->
      <div class="flex justify-center">
        <button
          @click="() => addChart()"
          class="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 border border-dashed border-muted-foreground/25 bg-background hover:bg-accent hover:text-accent-foreground h-24 w-full"
        >
          <div class="flex items-center space-x-2">
            <svg
              class="h-6 w-6"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M12 5v14" />
              <path d="M5 12h14" />
            </svg>
            <span>Add Chart</span>
          </div>
        </button>
      </div>
    </div>

    <!-- Empty state -->
    <div
      v-if="charts.length === 0"
      class="rounded-md border border-dashed p-8 text-center"
    >
      <svg
        class="mx-auto h-12 w-12 text-muted-foreground"
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M3 3v18h18" />
        <path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3" />
      </svg>
      <h3 class="mt-4 text-lg font-medium">No Charts Yet</h3>
      <p class="mt-2 text-muted-foreground">
        Click "Add Chart" to start analyzing symbols
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useRoute } from "vue-router";
import CandlestickChart from "@/components/CandlestickChart.vue";

interface Chart {
  id: string;
  symbol: string;
}

const route = useRoute();
const charts = ref<Chart[]>([]);

let chartIdCounter = 0;

const addChart = (symbol: string = "AAPL") => {
  const newChart: Chart = {
    id: `chart-${++chartIdCounter}`,
    symbol: symbol,
  };
  charts.value.push(newChart);
};

const removeChart = (id: string) => {
  const index = charts.value.findIndex((chart) => chart.id === id);
  if (index > -1) {
    charts.value.splice(index, 1);
  }
};

// Check for symbol in query parameter and add chart
onMounted(() => {
  const symbolFromQuery = route.query.symbol as string | undefined;
  
  if (symbolFromQuery) {
    // Check if a chart with this symbol already exists
    const existingChart = charts.value.find(chart => chart.symbol === symbolFromQuery);
    if (!existingChart) {
      addChart(symbolFromQuery);
    }
  } else if (charts.value.length === 0) {
    // Add default chart only if no symbol query param
    addChart();
  }
});
</script>
